import { useMemo } from 'react';
import { slotKey, useAssemblySlots } from './assemblySlots';
import { useCategories } from './catalogCategories';
import { useAssemblySteps } from './categoryBands';
import { isHardwarePart } from './hardwareInteractionsPrompt';
import type { WrittenInteraction } from './openaiBlenderApi';
import { namedIn, withLetter } from './partsCatalogue';
import { usePartsRun, type PartItem } from './partsRun';
import { pieceContents, useStepsRun } from './stepsRun';

// WHICH TOOL TURNS WHICH PIECE OF HARDWARE — read off the sub-steps, and asked of nobody: a
// sub-step lists its tools, and the hardware it is about is the hardware its sentence names
// ("Tighten the {4}") or adds.
//
// OR THAT THE PIECE IT ADDS HOLDS: "Insert the {101}", the screw with its washer on, names
// a piece and no part, and the Allen key that drives it was a tool used on nothing. Of all of
// it, the hardware the parts list gives a drive for (the screw, not its washer), where any has one.
//
// Two readers, which must agree: the tools subphase's table, and a tool's 3D build — built WITH what
// it drives, in one scene, so its tip and their recesses are cut against each other (see toolSet).

// A TOOL AND A PIECE OF HARDWARE IT TURNS, with every sub-step that has it do so — in the order the
// manual first uses the pair. `hardware` null: a tool used on nothing its sub-step's sentence names.
export type ToolPair = { tool: string; hardware: string | null; at: string[] };

const named = (text: string) => [...text.matchAll(/\{(\d+)\}/g)].map((m) => m[1]);

export function useToolPairs(docId: string): { items: PartItem[]; rows: ToolPair[] } {
  const { items } = usePartsRun(docId);
  const { steps } = useAssemblySteps(docId);
  const { subSteps } = useStepsRun(docId);
  const slots = useAssemblySlots(docId);
  // Subscribed so the rows are found once the ontology lands: until then no part reads as hardware.
  const cats = useCategories();

  const rows = useMemo(() => {
    const nameOf = new Map(slots.map((s) => [s.key, s.label]));
    const contents = pieceContents(steps, subSteps);
    const isHardware = (id: string) => !!items[Number(id)] && isHardwarePart(items[Number(id)]);
    const byPair = new Map<string, ToolPair>();
    for (const band of steps) {
      for (const sub of subSteps[band.category] ?? []) {
        if (!sub.tools?.length) continue;
        const said = new Set([
          ...named(sub.textEn), ...named(sub.textFr),
          ...sub.parts.filter((p) => p.role === 'added').map((p) => p.id),
        ]);
        const held = (id: string) => (contents.get(id) ?? []).filter(isHardware);
        // A piece only where it is the one ADDED: the piece something is driven into (the frame with
        // its barrel nut in) holds hardware this tool does not turn.
        const added = new Set(sub.parts.filter((p) => p.role === 'added').map((p) => p.id));
        const touched = [...new Set([...said].flatMap((id) => (items[Number(id)] ? [id].filter(isHardware)
          : added.has(id) ? held(id) : [])))];
        // OF THAT, WHAT THE TOOL TURNS: the hardware the parts list gives a drive for, where any has
        // one. "Slide the washer {F} onto the screw {E}" names both, and only the screw has a socket
        // for the Allen key; the washer and the cover beside it were rows of their own (Louis 2026-10-28).
        const driven = touched.filter((h) => driveText(items[Number(h)]) != null);
        const hardware = driven.length ? driven : touched;
        const where = nameOf.get(slotKey(band.category, sub.letter)) ?? `${band.num}-${sub.letter}`;
        for (const tool of sub.tools) {
          // AND OF THAT, WHAT ITS TIP FITS: in 2-2 the Allen key and the screwdriver are both used,
          // on the JCBC screw (hex socket) and the barrel nut (cross), and each was paired with both
          // (Louis 2026-10-28). Where its tip fits none of them, or is not known, all of them.
          const tip = tipOf(items[Number(tool)]);
          const fitting = hardware.filter((h) => fits(tip, items[Number(h)]));
          // A TOOL USED ON NOTHING THE SENTENCE NAMES is still a row: it is used, and on what is
          // the one thing nobody can say from here.
          for (const hw of fitting.length ? fitting : hardware.length ? hardware : [null]) {
            const key = `${tool}|${hw ?? ''}`;
            const row = byPair.get(key) ?? { tool, hardware: hw, at: [] };
            row.at.push(where);
            byPair.set(key, row);
          }
        }
      }
    }
    return [...byPair.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, steps, subSteps, slots, cats]);
  return { items, rows };
}

// THE KIND OF DRIVE A TEXT SAYS, in either language: a fastener's printed drive ("hex socket"), or
// a tool's name ("Allen key"). Null where it says none.
type DriveKind = 'hex' | 'torx' | 'cross' | 'slot';
const DRIVE_WORDS: [DriveKind, RegExp][] = [
  ['hex', /hex|allen|inbus|6 pans|six pans/i],
  ['torx', /torx|star|étoile/i],
  ['cross', /cross|phillips|pozi|cruciform|croix/i],
  ['slot', /slot|flat|plat/i],
];
const driveKind = (text: string | null | undefined): DriveKind | null =>
  DRIVE_WORDS.find(([, words]) => words.test(text ?? ''))?.[0] ?? null;

// A TOOL'S TIP: the drive the parts list gives it, or what its name says — an Allen key is hex, and
// a screwdriver with no more said is one of the two a screwdriver is (cross or slot). The parts
// list gives the tools of Josette no drive at all.
type Tip = DriveKind | 'screwdriver' | null;
function tipOf(tool: PartItem | undefined): Tip {
  if (!tool) return null;
  const names = [tool.nameEn, tool.nameFr, tool.name].filter(Boolean).join(' ');
  return driveKind(tool.size?.drive) ?? driveKind(names)
    ?? (/screwdriver|tournevis/i.test(names) ? 'screwdriver' : null);
}

const fits = (tip: Tip, hardware: PartItem | undefined): boolean => {
  const kind = driveKind(hardware?.size?.drive);
  if (!tip || !kind) return false;
  return tip === 'screwdriver' ? kind === 'cross' || kind === 'slot' : kind === tip;
};

// WHAT A TOOL DRIVES, piece by piece, in the order the manual first uses each — with the drive that
// turns it, as the parts list printed it. One answer for the table and the tool's build.
export type Driven = { id: string; part: PartItem; name: string; drive: string | null };

// "Hex · 4 mm", or null where the parts list says nothing of how the part is turned.
export const driveText = (p: PartItem | undefined) => [
  p?.size?.drive?.trim() || null,
  p?.size?.driveSizeMm != null ? `${p.size.driveSizeMm} mm` : null,
].filter(Boolean).join(' · ') || null;

export function useDriven(docId: string, partAt: number | null | undefined): Driven[] {
  const { items, rows } = useToolPairs(docId);
  return useMemo(() => {
    if (partAt == null) return [];
    const ids = [...new Set(rows.filter((r) => r.tool === String(partAt) && r.hardware != null)
      .map((r) => r.hardware as string))];
    return ids.flatMap((id) => {
      const part = items[Number(id)];
      if (!part) return [];
      return [{ id, part, name: withLetter(namedIn(part, 'en') || `#${id}`, part.letter), drive: driveText(part) }];
    });
  }, [rows, partAt, items]);
}

// WHAT A TOOL BUILT ON ITS OWN IS TOLD IT DRIVES — a tool whose hardware has no catalog row, and so
// cannot be built with it (see toolSet): each piece by name, with its printed size and the drive
// that turns it, in English, because this is prompt copy. One entry, flagged as a tool's: the
// server writes the rule that follows (the tip made for that drive, pointing down the axis the
// fastener goes in along — see openai_blender.interactions_block). Empty for a tool no sub-step
// puts on a named piece of hardware.
export function toolInteractions(driven: Driven[]): WrittenInteraction[] {
  const lines = driven.map(({ name, part, drive }) => {
    const said = [part.size?.spec, drive ? `${drive} drive` : null].filter(Boolean).join(', ');
    return said ? `${name} — ${said}` : name;
  });
  return lines.length ? [{ with: lines, through: [], how: '', tool: true }] : [];
}
